import { useAuth0 } from "@auth0/auth0-react";
import Fuse from "fuse.js";
import { useMemo } from "react";
import { useSearchParams } from "react-router";
import { SupabasePersonDetailsService } from "../../../modules/employees/server/supabase-person-service";
import { SupabaseMomentReviewLogService } from "../../../modules/moment/server/supabase-moment-review-service";
import { useSupabaseLoader } from "../../../server/supabase/hooks";

import type { MomentStatus } from "../../../modules/moment-card/types";
import type { ReviewStatus } from "../../../modules/moment/server/supabase-moment-review-service";
import type { ISO8601 } from "../../../utils/iso8601";
import type { UUID } from "../../../utils/uuid";

export type ReviewsVariant = "in-progress" | "previous";

export interface Review {
  moment_id: UUID;
  title: string;
  momentStatus: MomentStatus;
  reviewStatus: ReviewStatus;
  creator: string;
  requestedAt: ISO8601;
  message: string | null;
}

// The statuses a review passes through while it is still ours to act on.
const inProgressStatuses = new Set<ReviewStatus>([
  "IN_REVIEW",
  "COMMENTED",
  "APPROVED",
  "CHANGES_REQUESTED",
]);

// Creators can publish without the review being completed, so a published Communication is a
// previous review whatever state its review reached.
const publishedStatuses = new Set<MomentStatus>(["active", "finished"]);

const isPrevious = ({ momentStatus, reviewStatus }: Review): boolean =>
  publishedStatuses.has(momentStatus) || reviewStatus === "CANCELLED";

/**
 * The tabs hold different things, so they filter on different things: an in-progress review is
 * still moving through the review statuses, while a previous one is either a published
 * Communication or a review someone called off.
 */
export interface StatusFilterOption {
  value: string;
  label: string;
  field: "momentStatus" | "reviewStatus";
}

export const statusFilterOptions: Record<ReviewsVariant, StatusFilterOption[]> = {
  "in-progress": [
    { value: "IN_REVIEW", label: "In review", field: "reviewStatus" },
    { value: "COMMENTED", label: "Commented", field: "reviewStatus" },
    { value: "APPROVED", label: "Approved", field: "reviewStatus" },
    { value: "CHANGES_REQUESTED", label: "Changes requested", field: "reviewStatus" },
  ],
  previous: [
    { value: "active", label: "Active", field: "momentStatus" },
    { value: "finished", label: "Finished", field: "momentStatus" },
    { value: "CANCELLED", label: "Cancelled", field: "reviewStatus" },
  ],
};

export const useReviews = (
  variant: ReviewsVariant,
): { reviews: Review[]; loading: boolean; error: Error | null } => {
  const [searchParams] = useSearchParams();
  const status = searchParams.get("status") ?? "";
  const q = searchParams.get("q") ?? "";

  // Since useClaims doesn't return a user id or a email we use useAuth0()
  // The JWT has no person id on it, but we do get an email which we can use to find our person row.
  const currentEmail = useAuth0().user?.email;

  const { data, loading, error } = useSupabaseLoader(
    async ({ supabase, account_id }) => {
      if (!currentEmail) return [];

      const { data: people, error: peopleError } = await new SupabasePersonDetailsService(
        supabase,
      ).getAll(account_id, {
        order: [{ column: "id" }],
        filter: [{ key: "email", operator: "eq", value: currentEmail }],
        limit: 1,
      });

      if (peopleError) throw new Error(peopleError.message);

      const personId = people?.[0]?.id;
      if (!personId) return [];

      const { data: logs, error: logsError } = await new SupabaseMomentReviewLogService(
        supabase,
      ).getAllWithMoment(account_id, {
        order: [{ column: "created_at" }],
        filter: [{ key: "reviewer_person_id", operator: "eq", value: personId }],
      });

      if (logsError) throw new Error(logsError.message);

      // A Communication collects an entry per review step. Ordered oldest first, so the first entry
      // we see for one is the request: it names the creator, when they asked and what they said.
      const reviews = new Map<UUID, Review>();

      (logs ?? []).forEach(({ moment_id, created_at, note, user_name, moment }) => {
        if (reviews.has(moment_id)) return;
        if (!moment?.title || !moment.status || !moment.review_status) return;

        reviews.set(moment_id, {
          moment_id,
          title: moment.title,
          momentStatus: moment.status,
          // The moments view holds the current review state; the log holds how it started.
          reviewStatus: moment.review_status,
          creator: user_name,
          requestedAt: created_at,
          message: note,
        });
      });

      return [...reviews.values()].toSorted((a, b) => b.requestedAt.localeCompare(a.requestedAt));
    },
    [currentEmail],
  );

  // The query already returns every review assigned to us, so the filters only ever narrow what we
  // have: no need to go back to the database as they change.
  const reviews = useMemo(() => {
    const inTab = (data ?? []).filter((review) =>
      variant === "previous"
        ? isPrevious(review)
        : inProgressStatuses.has(review.reviewStatus) && !isPrevious(review),
    );

    // Ignoring a status the other tab set
    const selected = statusFilterOptions[variant].find((option) => option.value === status);
    const byStatus = selected
      ? inTab.filter((review) => review[selected.field] === selected.value)
      : inTab;

    return q.trim()
      ? new Fuse(byStatus, { keys: ["title", "creator"] }).search(q).map(({ item }) => item)
      : byStatus;
  }, [data, variant, status, q]);

  return { reviews, loading, error };
};
